import { Buffer } from 'buffer';
import { useState, useEffect, useCallback } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { PublicKey, Transaction, SystemProgram } from '@solana/web3.js';
import { web3 } from '@coral-xyz/anchor';

const PROGRAM_ID = new PublicKey('HkFoJ255o4TLHqSosxi2Yrg3wVr4saS9HFj8h6Z5wAnR');
const DEV_WALLET = new PublicKey('6Nueic7eZQ9QhN9Eu89ygxsrMM5wQoimQyzUBU4nca47');

export function useWalletRegistration() {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const [isRegistered, setIsRegistered] = useState(null); // null=unknown, true, false
  const [isChecking, setIsChecking] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);

  // Derive the WalletRegistry PDA for this wallet
  const getRegistryPDA = useCallback((walletKey) => {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('registry'), walletKey.toBuffer()],
      PROGRAM_ID
    )[0];
  }, []);

  // Check if wallet is registered on-chain
  const checkRegistration = useCallback(async () => {
    if (!publicKey || !connection) return;
    setIsChecking(true);
    try {
      const registryPDA = getRegistryPDA(publicKey);
      const accountInfo = await connection.getAccountInfo(registryPDA);
      setIsRegistered(accountInfo !== null);
    } catch (err) {
      console.error('Error checking registration:', err);
      setIsRegistered(false);
    } finally {
      setIsChecking(false);
    }
  }, [publicKey, connection, getRegistryPDA]);

  // Check registration when wallet connects
  useEffect(() => {
    if (publicKey) {
      checkRegistration();
    } else {
      setIsRegistered(null);
    }
  }, [publicKey, checkRegistration]);

  // Register wallet on-chain
  const registerWallet = useCallback(async () => {
    if (!publicKey || !sendTransaction || !connection) return;
    setIsRegistering(true);
    setRegistrationError(null);
    try {
      const registryPDA = getRegistryPDA(publicKey);
      // Build the register_wallet instruction manually using web3.js
      // since we have an older IDL format
      // The instruction discriminator for register_wallet is sha256("global:register_wallet")[0..8]
      const discriminator = Buffer.from([181, 141, 102, 82, 135, 213, 141, 8]);
      const keys = [
        { pubkey: registryPDA, isSigner: false, isWritable: true },
        { pubkey: publicKey, isSigner: true, isWritable: true },
        { pubkey: DEV_WALLET, isSigner: false, isWritable: true },
        { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      ];
      const ix = new web3.TransactionInstruction({
        programId: PROGRAM_ID,
        keys,
        data: discriminator,
      });
      const transaction = new Transaction().add(ix);
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature, 'confirmed');
      console.log('Wallet registered:', signature);
      setIsRegistered(true);
    } catch (err) {
      console.error('Registration error:', err);
      setRegistrationError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  }, [publicKey, sendTransaction, connection, getRegistryPDA]);

  return {
    isRegistered,
    isChecking,
    isRegistering,
    registrationError,
    registerWallet,
    checkRegistration,
  };
}
